/**
 * Admin → Company Research settings — typed api-client calls + TanStack Query
 * hooks (E07-F02-T06, web side).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). Transport uses the
 * shared `apiClient` (auto-injects the session Bearer token, normalises the
 * response, never throws) + `unwrap` (the single throw-at-the-Query-boundary
 * helper). Contracts come from `@eer/shared` (data-source-strategy.ts) so the SPA
 * speaks the exact same shapes the Fastify route validates.
 *
 * Endpoints (1:1 with the legacy prototype research.proto.js setStrategy / setGate
 * over the new REST surface — research-data-source.ts):
 *   GET  /research/settings                   → useResearchSettings()
 *   PUT  /research/settings                    → useSaveResearchSettings()
 *                                               (activeStrategy / reviewGate /
 *                                                discoveryLimits — E07-F02-T13)
 *   POST /research/strategies/:key/activate     → useActivateStrategy()
 *
 * The PUT carries `{ activeStrategy?, reviewGate? }`; an attempt to activate a
 * strategy whose `isAvailable===false` is rejected server-side with
 * `STRATEGY_UNAVAILABLE` (409) — `unwrap` surfaces that as an `ApiResultError`
 * the tab renders as a toast.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  researchSettingsResponseSchema,
  strategyDtoSchema,
  type ResearchSettingsResponse,
  type ResearchSaveSettingsInput,
  type StrategyDTO,
  type StrategyKey,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** Feature-local query key (kept out of the shared registry). */
export const RESEARCH_SETTINGS_QUERY_KEY = ['research', 'settings'] as const;

/** GET /research/settings — strategies[] + activeStrategy + reviewGate + weightProfile. */
async function fetchResearchSettings(): Promise<ResearchSettingsResponse> {
  const result = await apiClient.get<unknown>('/research/settings', {
    command: 'research_get_settings',
  });
  return researchSettingsResponseSchema.parse(unwrap(result));
}

/** PUT /research/settings — change active strategy and/or review gate. */
async function putResearchSettings(
  input: ResearchSaveSettingsInput,
): Promise<ResearchSettingsResponse> {
  const result = await apiClient.put<unknown>('/research/settings', input, {
    command: 'research_save_settings',
  });
  return researchSettingsResponseSchema.parse(unwrap(result));
}

/** POST /research/strategies/:key/activate — atomic one-active flip. */
async function postActivateStrategy(key: StrategyKey): Promise<StrategyDTO> {
  const result = await apiClient.post<unknown>(
    `/research/strategies/${encodeURIComponent(key)}/activate`,
    undefined,
    { command: 'research_save_settings' },
  );
  return strategyDtoSchema.parse(unwrap(result));
}

/** The Settings → Company Research tab state. */
export function useResearchSettings(): UseQueryResult<ResearchSettingsResponse, ApiResultError> {
  return useQuery<ResearchSettingsResponse, ApiResultError>({
    queryKey: RESEARCH_SETTINGS_QUERY_KEY,
    queryFn: fetchResearchSettings,
  });
}

/** Save mutation — invalidates the settings query so the saved choice re-renders. */
export function useSaveResearchSettings(): UseMutationResult<
  ResearchSettingsResponse,
  ApiResultError,
  ResearchSaveSettingsInput
> {
  const queryClient = useQueryClient();
  return useMutation<ResearchSettingsResponse, ApiResultError, ResearchSaveSettingsInput>({
    mutationFn: putResearchSettings,
    onSuccess: (data) => {
      // Seed the cache with the authoritative server state (avoids a flash).
      queryClient.setQueryData(RESEARCH_SETTINGS_QUERY_KEY, data);
      void queryClient.invalidateQueries({ queryKey: RESEARCH_SETTINGS_QUERY_KEY });
    },
  });
}

/** Dedicated activate mutation (radio click) — same invalidation as save. */
export function useActivateStrategy(): UseMutationResult<StrategyDTO, ApiResultError, StrategyKey> {
  const queryClient = useQueryClient();
  return useMutation<StrategyDTO, ApiResultError, StrategyKey>({
    mutationFn: postActivateStrategy,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RESEARCH_SETTINGS_QUERY_KEY });
    },
  });
}
